import './LicenseInfo.css';

import React, { useContext, useEffect, useState } from 'react';
import B2BFormContainer from '../../../b2b-common/B2BFormContainer';
import { Cancel, Save } from '@mui/icons-material';
import { LoginContext } from '../../../../App';
import { OrganizationDetailsContext } from '../../forms/OrganizationDetailsModal';
import { generateTextField } from '../../../b2b-common/helpers';
import LicenseDateRangePicker from './LicenseDateRangePicker';
import dayjs from 'dayjs';
import LicenseProductsList from './LicenseProductsList';
import { createOrganizationLicense, enableDisableOrganizationLicense, fetchProductsData, removeOrganizationLicense, updateOrganizationLicense } from '../../../../services/b2b-users-apiService';
import SwitchWithTooltip from '../../../b2b-common/SwitchWithTooltip';

import { UsersContext } from '../../B2BUsers';

const LicenseInfo = React.forwardRef(({ initialLicense, isNewLicense, onClose, unavailableDateRanges, onLicenseUpdate, onLicenseRemove }, ref) => {

    const { loginData } = useContext(LoginContext);
    const { organizationUUID } = useContext(OrganizationDetailsContext);
    const { productsData, setProductsData } = useContext(UsersContext);

    const [license, setLicense] = useState(initialLicense);
    const [loadingEnableDisableLicense, setLoadingEnableDisableLicense] = useState(false);
    const [loadingSaveLicenseInfo, setLoadingSaveLicenseInfo] = useState(false);
    const [loadingRemoveLicense, setLoadingRemoveLicense] = useState(false);
    const [fetchingProductsData, setFetchingProductsData] = useState(false);
    const [licenseProducts, setLicenseProducts] = useState([]);
    const [updatedProducts, setUpdatedProducts] = useState({
        includedProducts: [],
        newlyAddedProducts: [],
        toRemoveProducts: [],
    });

    const fetchProducts = async () => {
        try {
            if ((loginData) && (!productsData)) {
                const { accessToken } = loginData;
                setFetchingProductsData(true);
                const data = await fetchProductsData(accessToken);
                setProductsData(data);
            }
        } catch (error) {
            console.error('Failed to fetch products data:', error.message);
            alert('Failed to fetch products data. Please try again.');
        } finally {
            setFetchingProductsData(false);
        }
    };

    useEffect(() => {
        setLicense(initialLicense);
        const destructuredProducts =
            initialLicense.products.map(({ parentProduct, childProducts, otherImages, ...product }) => product);
        setLicenseProducts(destructuredProducts);
        fetchProducts();
    }, [initialLicense]);

    const handleRemoveLicense = async () => {
        setLoadingRemoveLicense(true);
        try {
            const { accessToken } = loginData;
            const licenseId = license.licenseId;
            await removeOrganizationLicense(organizationUUID, licenseId, accessToken);
            onLicenseRemove(license);
            onClose();
        } catch (error) {
            console.error('Failed to remove license:', error.message);
            alert('Failed to remove license. Please try again.');
        } finally {
            setLoadingRemoveLicense(false);
        }
    }

    const handleCreateLicense = async () => {
        setLoadingSaveLicenseInfo(true);
        try {
            const { accessToken } = loginData;
            const updatedLicense = {
                ...license,
                products: calculateUpdatedProducts(),
            };
            const response = await createOrganizationLicense(organizationUUID, updatedLicense, accessToken);
            setLicense(response);
            onLicenseUpdate(response);
            onClose();
        } catch (error) {
            console.error('Failed to create license:', error.message);
            alert('Failed to create license. Please try again.');
        } finally {
            setLoadingSaveLicenseInfo(false);
        }
    }

    const calculateUpdatedProducts = () => {
        const updatedProductsList = licenseProducts
            .filter(product => !updatedProducts.toRemoveProducts.some(removeProduct => removeProduct.productId === product.productId))
            .concat(updatedProducts.newlyAddedProducts);
        return updatedProductsList;
    };

    const handleUpdateLicense = async () => {
        setLoadingSaveLicenseInfo(true);
        try {
            const { accessToken } = loginData;
            const licenseId = license.licenseId;
            const updatedLicense = {
                ...license,
                products: calculateUpdatedProducts(),
            };
            const response = await updateOrganizationLicense(organizationUUID, licenseId, updatedLicense, accessToken);
            setLicense(response);
            onLicenseUpdate(response);
            onClose();
        } catch (error) {
            console.error('Failed to update license:', error.message);
            alert('Failed to update license. Please try again.');
        } finally {
            setLoadingSaveLicenseInfo(false);
        }
    }

    const handleSwitchToggle = async () => {
        setLoadingEnableDisableLicense(true);
        try {
            const { accessToken } = loginData;
            const licenseId = license.licenseId;
            const isEnabled = !license.enabled;
            const updatedLicense = await enableDisableOrganizationLicense(organizationUUID, licenseId, isEnabled, accessToken);
            setLicense(updatedLicense);
            onLicenseUpdate(updatedLicense);
        } catch (error) {
            console.error('Failed to enable/disable license:', error.message);
            alert('Failed to enable/disable license. Please try again.');
        } finally {
            setLoadingEnableDisableLicense(false);
        }
    }

    const handleLicenseProductsChange = (updatedProducts) => {
        setUpdatedProducts(updatedProducts);
    }

    const buttonsConfig = [
        {
            label: 'Remove License',
            confirm: true,
            onConfirm: handleRemoveLicense,
            isLoading: loadingRemoveLicense,
            color: 'secondary',
            variant: 'contained',
            key: '2',
            icon: <Cancel />,
        },
        {
            label: 'Save & Close',
            onClick: isNewLicense ? handleCreateLicense : handleUpdateLicense,
            isLoading: loadingSaveLicenseInfo,
            color: 'primary',
            variant: 'contained',
            key: '1',
            icon: <Save />,
        },
    ];

    return (
        <B2BFormContainer ref={ref} title={`${license.licenseCode}`} onClose={onClose} buttonsConfig={buttonsConfig}>
            <div className="license-info-container">
                {generateTextField("License Code", license.licenseCode, "license-code")}
                <LicenseDateRangePicker
                    className="license-date-range-picker"
                    startLabel="Start Date"
                    endLabel="End Date"
                    selectedStartDate={dayjs(license.startDate)}
                    selectedEndDate={dayjs(license.endDate)}
                    handleDateChange={(date, type) => {
                        setLicense((prevLicense) => {
                            if (type === 'start') {
                                return {
                                    ...prevLicense,
                                    startDate: dayjs(date).format('YYYY-MM-DD'),
                                };
                            }
                            return {
                                ...prevLicense,
                                endDate: dayjs(date).format('YYYY-MM-DD'),
                            };
                        });
                    }}
                    unavailableDateRanges={unavailableDateRanges}
                />
                {!isNewLicense &&
                    <SwitchWithTooltip
                        tooltipTitle={`Is license enabled?`}
                        tooltipPlacement="right"
                        className={"license-enabled-switch"}
                        checked={license.enabled}
                        onChange={handleSwitchToggle}
                        loading={loadingEnableDisableLicense}
                    />
                }
                <LicenseProductsList
                    className="license-products-list"
                    productsData={productsData}
                    includedProducts={licenseProducts}
                    isFetching={fetchingProductsData}
                    onProductsChange={handleLicenseProductsChange}
                />
            </div>

        </B2BFormContainer>
    );

});

LicenseInfo.displayName = 'LicenseInfo';

export default LicenseInfo;
